import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { FieldType, FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { MatTabsModule } from '@angular/material/tabs';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { FuseCardComponent } from '../../../../@fuse/components/card';
import {
  DETAILS_SERVICE_TOKEN,
  DetailsService,
} from '../../../features/admin/modules/modules-entity/templates/details/details/details.service';
import { takeUntil } from 'rxjs/operators';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { InterfaceActionButtonComponent } from '../interface-action-button.component';
import type { InterfaceActionContext } from '../../../features/admin/modules/features/interface-actions/interface-actions.directive';
import { InterfaceActionsService } from '../../../features/admin/modules/features/interface-actions/interface-actions.service';
import { EntriesService } from '../../../features/admin/modules/features/entries/entries.service';
import { ModulesStore } from '../../../features/admin/modules/modules.store';

@Component({
  selector: 'formly-field-pages',
  template: `
    <!-- Desktop sidebar -->
    <!-- Figma splits the 1136px content row as 265 rail + 74 gutter + 798 card. The rail used to be
         w-80 (320px) with a gap-4 (16px) gutter, which left the two panels almost touching. -->
    <div class="flex flex-col md:flex-row gap-4 md:gap-[74px] mb-4 min-w-0">
      <!-- top offset: stick below the details page's sticky save toolbar (var set on the
           details component host; 0 when this field renders outside a details page). -->
      <fuse-card
        class="hidden md:flex flex-0 md:w-[265px] border self-start sticky z-10"
        style="top: var(--dd-detail-toolbar-height, 0px)">

        <div class="w-full">
          @for (page of field.fieldGroup; track trackByFn($index, page); let i = $index) {
            @if (page.hide !== true && page.className !== 'hidden') {
              <button
                type="button"
                [attr.data-cy]="'subpage-' + page.key"
                [class.dd-rail-item--active]="page.key === selectedPage?.key"
                (click)="goToPanel(page)"
                class="dd-rail-item w-full px-4 py-4 cursor-pointer first:border-t-0 border-t text-left">
                <span class="page-nav-label">{{ page.props.label }}</span>
              </button>
            }
          }
        </div>
      </fuse-card>

      <!-- Mobile scrollable buttons -->
      <div class="block md:hidden w-full">
        <div class="overflow-x-auto scrollbar-hide">
          <div class="flex gap-2 pb-2 min-w-min px-2 mt-2">
            @for (page of field.fieldGroup; track trackByFn($index, page); let i = $index) {
              @if (page.hide !== true && page.className !== 'hidden') {
                <button
                  mat-flat-button
                  data-cy="'subpage-'{{ page.props.label }}"
                  [class.bg-primary]="selectedPage?.key === page.key"
                  [disabled]="page.hide === true || page.className === 'hidden'"
                  (click)="goToPanel(page)"
                  class="outline-none ring-2 ring-primary small-button">
                  {{ page.props.label }}
                </button>
              }
            }
          </div>
        </div>
      </div>

      <!-- flex-1 does the sizing; the old w-[calc(100%-22rem)] hardcoded the previous rail width
           and fought it, which is why the card came out 4px wider than the rail+gutter allowed. -->
      <div class="w-full md:flex-1 min-w-0">
        @for (page of field.fieldGroup; track trackByFn($index, page); let i = $index) {
          @if (page.hide !== true && page.className !== 'hidden' && selectedPage?.key === page.key) {
            <fuse-card class="w-full min-w-0 border px-6 py-4">
              <div class="w-full">
                <!-- Card header (Figma 2652-117626): icon + title belong here, not per section —
                     sections (below) only carry a label. -->
                <div class="flex items-center justify-between gap-4 mb-4">
                  <div class="flex items-center gap-2 min-w-0">
                    @if (page.props.icon) {
                      <span
                        class="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-primary/10 dark:bg-primary/20">
                        <mat-icon class="icon-size-4 text-primary" [svgIcon]="page.props.icon"></mat-icon>
                      </span>
                    }
                    <h3 class="truncate">{{ page.props.label }}</h3>
                  </div>
                  @if (pageActionContext(page); as actionContext) {
                    <app-interface-action-button
                      class="flex-none"
                      [buttonData]="actionContext"
                      [context]="'formButton'"
                      buttonSize="small"
                      (handleClick)="executePageAction(page)">
                    </app-interface-action-button>
                  }
                </div>
                <formly-field [field]="page"></formly-field>
              </div>
            </fuse-card>
          }
        }
      </div>
    </div>
  `,
  styles: `
    .scrollbar-hide {
      -ms-overflow-style: none;
      scrollbar-width: none;
    }
    .scrollbar-hide::-webkit-scrollbar {
      display: none;
    }
    .overflow-x-auto {
      scroll-behavior: smooth;
    }
    /* Active page indicator (#20843): accent text + a filled accent plate, replacing the old
       underline. Figma "Section menu/tab", State=Active (node 818:38803): fill = brand
       primary @ 10% opacity over white, label = brand primary, no corner radius (the row
       keeps the existing bottom divider full-bleed). Uses the --fuse-primary(-rgb) chain so
       it tracks per-tenant branding, same alpha-ladder pattern as _table.scss row hover. */
    .dd-rail-item {
      transition:
        background-color 150ms ease,
        color 150ms ease;
    }
    /* Hover reuses the list-row brand-alpha ladder (_table.scss: 3% light / 6% dark) instead
       of the old static grey, so rail and list hover read as one system per tenant. */
    .dd-rail-item:hover:not(.dd-rail-item--active) {
      background-color: rgba(var(--fuse-primary-rgb), 0.03);
    }
    :host-context(.dark) .dd-rail-item:hover:not(.dd-rail-item--active) {
      background-color: rgba(var(--fuse-primary-rgb), 0.06);
    }
    .dd-rail-item--active {
      color: var(--fuse-primary);
      background-color: rgba(var(--fuse-primary-rgb), 0.1);
    }
    /* Dark needs a LIGHTER label, not the same brand colour: a tenant primary tuned for white
       (e.g. #3100FF) lands at 2:1 on its own dark plate and is unreadable. Figma shows the same
       tab in dark with a lightened tint (#9C9DFF over #211A40). Mixed off --fuse-primary rather
       than hardcoded, so it still tracks per-tenant branding — including tenants that already
       configure their own dark primary, where the mix stays close to what they picked. */
    :host-context(.dark) .dd-rail-item--active {
      color: color-mix(in srgb, var(--fuse-primary) 45%, #ffffff);
      background-color: rgba(var(--fuse-primary-rgb), 0.16);
    }
  `,
  imports: [FormlyModule, MatTabsModule, MatButton, MatIcon, FuseCardComponent, InterfaceActionButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyFieldPages extends FieldType implements OnInit {
  detailsService = inject(DETAILS_SERVICE_TOKEN) as DetailsService;
  selectedPage;
  cdr = inject(ChangeDetectorRef);
  private destroy$ = injectDestroy();
  readonly #modulesStore = inject(ModulesStore);
  readonly #entriesService = inject(EntriesService);
  readonly #interfaceActionsService = inject(InterfaceActionsService);
  readonly #route = inject(ActivatedRoute);
  readonly #router = inject(Router);

  trackByFn(index: number, item: any): any {
    return item.id;
  }

  ngOnInit() {
    const visiblePages = this.field.fieldGroup.filter(page => page.hide !== true && page.className !== 'hidden');
    // Deep-link support (#20880): a shared/bookmarked URL can carry ?subpage=<key> to land on a
    // specific tab (e.g. "voorkeuren") instead of always defaulting to the first one.
    const requestedKey = this.#route.snapshot.queryParamMap.get('subpage');
    this.selectedPage = visiblePages.find(page => page.key === requestedKey) ?? visiblePages[0];
    this.detailsService.updatePage$.next(this.selectedPage);
    // needed to update the view when the formControl value changes (for hiding pages)
    this.field.formControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.cdr.markForCheck();
    });
  }

  /** Resolves the module carrying the page's configured card-header action (locked to its own form/module). */
  #pageActionModule(page: FormlyFieldConfig) {
    const moduleCode = page.props?.['interfaceActionModuleCode'];
    return moduleCode ? this.#modulesStore.enabledModules().find(m => m.code === moduleCode) : undefined;
  }

  /**
   * The configured card-header action for `page`, resolved and role-filtered — undefined hides the
   * button entirely (no action configured, or the current user's role excludes it).
   */
  pageActionContext(page: FormlyFieldConfig): InterfaceActionContext | undefined {
    const actionId = page.props?.['interfaceActionId'];
    if (!actionId) {
      return undefined;
    }
    // No saved entry (new, unsaved detail page) → no button. Without this gate the action
    // would run with entry undefined and handleCustomAction's id==null branch would CREATE
    // a phantom entry; mirrors the kebab menu's @if (entry()?.id) render gate.
    const entry = this.detailsService.entry();
    if (!entry?.id) {
      return undefined;
    }
    const action = this.#pageActionModule(page)?.interfaceActions?.find(a => a.id === actionId);
    // isAllowedForCurrentUser fails OPEN on a missing action (see its doc comment), so an unresolved
    // actionId must be rejected explicitly before that role check.
    if (!action || !this.#interfaceActionsService.isAllowedForCurrentUser(action)) {
      return undefined;
    }
    return { interfaceAction: action, entry, entries: [entry] };
  }

  executePageAction(page: FormlyFieldConfig): void {
    const context = this.pageActionContext(page);
    const module = this.#pageActionModule(page);
    if (!context || !module) {
      return;
    }
    // Same guard runner as the ⋮ menu (invalid-form gate + confirmation) — the two surfaces
    // offer the same actions, so they must not diverge (PR 9479 review B6).
    this.#interfaceActionsService.runWithGuards(context.interfaceAction, this.detailsService.form(), () =>
      this.#entriesService.processAction(
        { name: 'custom', id: context.entry?.id, entry: context.entry, interfaceAction: context.interfaceAction },
        module
      )
    );
  }

  goToPanel(page: FormlyFieldConfig) {
    event.preventDefault();
    this.selectedPage = page;
    this.detailsService.updatePage$.next(this.selectedPage);
    // Keep ?subpage in sync so the active tab is shareable/bookmarkable and survives a refresh.
    // replaceUrl: a tab click is not a navigation the user expects on the back-button stack.
    this.#router.navigate([], {
      relativeTo: this.#route,
      queryParams: { subpage: page.key },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
}
